import { MotionValue, motion, useScroll, useTransform } from "framer-motion";
import React, { useEffect, useRef, useLayoutEffect, useState } from "react";
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import PlaySpin from './playspin';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub, faCodepen, faHackerrank, faDribbble, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { faPlay, faPause } from '@fortawesome/free-solid-svg-icons';

export default function Hero() {
    const container = useRef<HTMLElement | null>(null);

    const { scrollYProgress } = useScroll({
        target: container,
        offset: ["start start", "end end"],
    });

    useEffect(() => {
        const lenis = new Lenis();

        function raf(time: number) {
            lenis.raf(time);
            requestAnimationFrame(raf);
        }

        requestAnimationFrame(raf);
    }, []);

    return (
        <section ref={container} className="relative h-[200%] md:h-[200vh]">
            <Section1 scrollYProgress={scrollYProgress} />
            <Section2 scrollYProgress={scrollYProgress} />
        </section>
    );
}

const Section1 = ({ scrollYProgress }: { scrollYProgress: MotionValue<number> }) => {
    const firstTextTop = useRef(null);
    const secondTextTop = useRef(null);
    const sliderTop = useRef(null);

    let xPercent = 0;
    let direction = -1;

    useLayoutEffect(() => {
        gsap.registerPlugin(ScrollTrigger);
        gsap.to(sliderTop.current, {
            scrollTrigger: {
                trigger: document.documentElement,
                scrub: 0.25,
                start: 0,
                end: window.innerHeight,
                onUpdate: e => direction = e.direction * -1,
            },
            x: "-500px",
        });
        requestAnimationFrame(animate);
    }, []);

    const [windowWidth, setWindowWidth] = useState(typeof window === 'undefined' ? 0 : window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(typeof window === 'undefined' ? 0 : window.innerWidth);
        if (typeof window !== 'undefined') {
            window.addEventListener("resize", handleResize);
        }
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const animate = () => {
        if (xPercent < -100) xPercent = 0;
        else if (xPercent > 0) xPercent = -100;

        gsap.set(firstTextTop.current, { xPercent: xPercent });
        gsap.set(secondTextTop.current, { xPercent: xPercent });

        requestAnimationFrame(animate);
        xPercent += 0.1 * direction;
    };

    const bottom = useTransform(scrollYProgress, [0, 1], ["8px", "18px"]);
    const left = useTransform(scrollYProgress, [0, 1], ["8px", "18px"]);

    const width = useTransform(scrollYProgress, [0, 1], windowWidth > 1400 ? ["120px", "140px"] : windowWidth > 767 ? ["72px", "84px"] : ["32px", "42px"]);
    const height = useTransform(scrollYProgress, [0, 1], windowWidth > 1400 ? ["120px", "140px"] : windowWidth > 767 ? ["72px", "84px"] : ["32px", "42px"]);

    return (
        <motion.div
            className="sticky top-0 h-[100%] md:h-screen md:pb-[10vh]"
            style={{
                backgroundImage: `url("../../hero/hero-bg.svg")`,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
            }}
        >
            <div className="h-screen w-full lg:w-screen overflow-hidden relative">
                <div className="w-[100vw] flex flex-col absolute top-[50%] translate-y-[-50%]">
                    <div ref={sliderTop} className="flex font-semibold text-smoke font-jedira">
                        <p
                            className="text-nowrap text-9xl md:text-[12rem] xl:text-[20rem]"
                            ref={firstTextTop}
                        >
                            SURYA FRONTEND DEVELOPER -
                        </p>
                        <p
                            className="text-nowrap text-9xl md:text-[12rem] xl:text-[20rem]"
                            ref={secondTextTop}
                        >
                            - SURYA FRONTEND DEVELOPER -
                        </p>
                    </div>
                </div>
                <motion.div style={{ bottom, left, width, height }} className="absolute overflow-hidden">
                    <img
                        className="w-full h-full"
                        src={"../../pettern/arrow-up.png"}
                        width={200}
                        height={200}
                        alt="arrow"
                    />
                </motion.div>

                <div className="border border-smoke py-1 px-2 absolute right-4 bottom-4 bg-lemone">
                    <p className="text-base lg:text-xl 2xl:text-2xl 3xl:text-3xl 4xl:text-4xl font-nebulica">2024</p>
                </div>
            </div>
        </motion.div>
    );
};

const Section2 = ({ scrollYProgress }: { scrollYProgress: MotionValue<number> }) => {
    const [windowWidth, setWindowWidth] = useState(typeof window === 'undefined' ? 0 : window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(typeof window === 'undefined' ? 0 : window.innerWidth);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [isPlaying, setIsPlaying] = useState(false);
    const audioSource = '../../introduction.mp3';
    const [icon, setIcon] = useState(faPlay);

    const handleButtonPlay = () => {
        setIsPlaying(!isPlaying);
        setIcon(isPlaying ? faPlay : faPause);
    };

    const socials = [
        { icon: faGithub, link: 'https://github.com/van-surya' },
        { icon: faCodepen, link: 'https://codepen.io/dawnscript' },
        { icon: faHackerrank, link: 'https://www.hackerrank.com/profile/van_surya' },
        { icon: faDribbble, link: 'https://dribbble.com/van_surya' },
        { icon: faYoutube, link: 'https://www.youtube.com/@dawnscript?si=n8Qqt9lp8h8m23d_' },
    ];

    const translateImageX = useTransform(scrollYProgress, [0, 1], windowWidth > 767 ? [-100, 0] : [-100, 0]);
    const translateImageY = useTransform(scrollYProgress, [0, 1], windowWidth > 767 ? [100, 0] : [-100, 0]);

    const scalePlay = useTransform(scrollYProgress, [0, 1], windowWidth > 767 ? [0, 1] : [0, 0.8]);
    const opacityPlay = useTransform(scrollYProgress, [0, 1], [0, 1]);

    const scaleSocial = useTransform(scrollYProgress, [0, 1], [0, 1]);
    const translateSocialY = useTransform(scrollYProgress, [0, 1], ['0', '-5vh']);

    return (
        <motion.div className="relative h-screen w-screen overflow-hidden">
            <img
                className="absolute bottom-0 left-[50%] translate-x-[-50%] w-[370px] h-[370px] object-cover md:w-[640px] md:h-auto lg:w-auto lg:h-[calc(100vh-200px)]"
                src={"../../hero/hero.webp"}
                width={1045}
                height={1045}
                alt="Hero Surya"
            />

            <motion.div
                style={{ scale: scalePlay, translateX: translateImageX, translateY: translateImageY, opacity: opacityPlay }}
                className="absolute top-[40%] right-[10%] md:top-[30%] md:right-[30%] lg:top-[10vh] z-10 bg-lemone border-lemone border-4 rounded-full lg:border-0 lg:bg-transparent lg:border-transparent lg:rounded-none"
            >
                <PlaySpin handleButtonPlay={handleButtonPlay} icon={icon} audioSource={audioSource} isPlaying={isPlaying} />
            </motion.div>

            <motion.div
                style={{ scale: scaleSocial, translateY: translateSocialY }}
                className="absolute bottom-0 left-0 w-[100%] flex justify-center z-20"
            >
                <div className="w-fit flex flex-row justify-center gap-3 bg-glass-one py-2 px-6 rounded-full">
                    {socials.map((social, x) => (
                        <motion.div
                            key={x}
                            whileHover={{ scale: 1.3, y: -15 }}
                            transition={{
                                type: "spring",
                                bounce: 0.4,
                                duration: 0.8,
                            }}
                        >
                            <a
                                href={social.link}
                                className="w-[42px] h-[42px] rounded-md flex items-center justify-center text-[1.7rem] text-primary hover:bg-primary hover:text-smoke"
                            >
                                <FontAwesomeIcon icon={social.icon} />
                            </a>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </motion.div>
    );
};